import React, { useEffect, useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button,
  InputBase, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from "notistack";
import Cookies from "js-cookie";
import AdminLayout from '../../components/admin/AdminLayout';
import instance from './../../axios/instance';
import { t } from 'i18next';
import { tableCellClasses } from '@mui/material/TableCell';

function AdminPackages() {
  const [search, setSearch] = useState("");
  const [packages, setPackages] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const lang = Cookies.get("i18next") || "en";

  // fetch all packages once on mount
  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await instance.get("packages/get-packages");
      setPackages(response.data.data);
    } catch (error) {
      enqueueSnackbar(t("fetchError"), { variant: "error" });
    }
  };

  // Filtered result based on search
  const filteredPackages = useMemo(() => {
    return packages.filter(pkg =>
      (lang === "ar" ? pkg.titleAr : pkg.titleEn).toLowerCase().includes(search.toLowerCase())
    );
  }, [packages, search, lang]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;

    try {
      await instance.delete(`packages/delete-package/${id}`);
      setPackages(prev => prev.filter(pkg => pkg.id !== id)); // update UI without reload
      enqueueSnackbar(t("deleteSuccess"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(t("deleteError"), { variant: "error" });
    }
  };

  return (
    <AdminLayout>
      <TableContainer component={Paper}>
        {/* Search bar */}
        <Paper sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: "100%" }}>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder={t("adminSearchPackage")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>

        {/* Table */}
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("packName")}</StyledTableCell>
              <StyledTableCell>{t("teacherName")}</StyledTableCell>
              <StyledTableCell>{t("level")}</StyledTableCell>
              <StyledTableCell>{t("price")}</StyledTableCell>
              <StyledTableCell>{t("currency")}</StyledTableCell>
              <StyledTableCell>{t("delete")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPackages.length > 0 ? (
              filteredPackages.map((pack) => (
                <StyledTableRow key={pack.id}>
                  <StyledTableCell>
                    {lang === "ar" ? pack.titleAr : pack.titleEn}
                  </StyledTableCell>
                  <StyledTableCell>
                    {pack.teacher.firstName} {pack.teacher.lastName}
                  </StyledTableCell>
                  <StyledTableCell>
                    {lang === "ar" ? pack.level.titleAR : pack.level.titleEN}
                  </StyledTableCell>
                  <StyledTableCell>{pack.price}</StyledTableCell>
                  <StyledTableCell>{t(pack.currency)}</StyledTableCell>
                  <StyledTableCell>
                    <Button variant="contained" color="error" onClick={() => handleDelete(pack.id)}>
                      {t("delete")}
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t("noPackagesFound")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </AdminLayout>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export default AdminPackages;
